import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, keyframes, useTheme } from '@mui/material/styles';

/* ── Motion (desktop only) ──────────────────────────────── */

const drift = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) rotate(var(--r, 0deg)); }
  25% { transform: translate3d(14px, -22px, 0) rotate(calc(var(--r, 0deg) + 8deg)); }
  50% { transform: translate3d(-6px, -34px, 0) rotate(calc(var(--r, 0deg) - 4deg)); }
  75% { transform: translate3d(-16px, -12px, 0) rotate(calc(var(--r, 0deg) + 5deg)); }
`;

const spinSlow = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const floatUp = keyframes`
  0% { transform: translateY(0) scale(0.6); opacity: 0; }
  10% { opacity: 0.85; }
  55% { transform: translateY(-50vh) scale(1.15); opacity: 0.5; }
  100% { transform: translateY(-105vh) scale(0.5); opacity: 0; }
`;

const pulseGlow = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.45; }
  50% { transform: scale(1.18); opacity: 0.9; }
`;

const prismSweep = keyframes`
  0% { transform: translateX(-30%) rotate(-8deg); opacity: 0; }
  20% { opacity: 0.55; }
  50% { transform: translateX(10%) rotate(-8deg); opacity: 0.35; }
  80% { opacity: 0.5; }
  100% { transform: translateX(50%) rotate(-8deg); opacity: 0; }
`;

const facetShine = keyframes`
  0%, 100% { opacity: 0.35; filter: brightness(1) saturate(1); }
  40% { opacity: 0.85; filter: brightness(1.45) saturate(1.2); }
  70% { opacity: 0.5; filter: brightness(1.1) saturate(1); }
`;

const sparkBurst = keyframes`
  0%, 100% { transform: scale(0.2) rotate(0deg); opacity: 0; }
  35% { transform: scale(1.15) rotate(25deg); opacity: 1; }
  65% { transform: scale(0.75) rotate(50deg); opacity: 0.4; }
`;

const caustic = keyframes`
  0%, 100% { background-position: 0% 40%, 100% 60%; }
  50% { background-position: 80% 20%, 20% 80%; }
`;

const breathe = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.75; transform: scale(1.06); }
`;

const BLOOMS = [
  { size: 480, top: '-8%', left: '-12%', delay: 0, color: '#E8C878' },
  { size: 380, top: '48%', right: '-14%', delay: 1.6, color: '#D4A574' },
  { size: 260, top: '22%', left: '52%', delay: 2.4, color: '#F5E6C8' },
  { size: 200, top: '72%', left: '18%', delay: 0.9, color: '#B8894A' },
];

const CRYSTALS = [
  { top: '8%', left: '8%', size: 52, delay: 0, duration: 16, rotate: 12 },
  { top: '14%', left: '78%', size: 40, delay: 1.4, duration: 18, rotate: -28 },
  { top: '58%', left: '6%', size: 46, delay: 2.8, duration: 15, rotate: 36 },
  { top: '66%', left: '72%', size: 58, delay: 0.7, duration: 20, rotate: -16 },
  { top: '34%', left: '88%', size: 34, delay: 3.2, duration: 14, rotate: 48 },
  { top: '42%', left: '38%', size: 30, delay: 2, duration: 19, rotate: -42 },
  { top: '80%', left: '42%', size: 38, delay: 1.1, duration: 17, rotate: 22 },
];

const RINGS = [
  { top: '18%', left: '62%', size: 90, delay: 0 },
  { top: '55%', left: '22%', size: 70, delay: 2.2 },
  { top: '75%', left: '78%', size: 56, delay: 4 },
];

const SPARKLES = [
  { top: '12%', left: '28%', delay: 0, s: 14 },
  { top: '24%', left: '58%', delay: 1.2, s: 10 },
  { top: '40%', left: '14%', delay: 2.4, s: 12 },
  { top: '52%', left: '82%', delay: 0.8, s: 11 },
  { top: '68%', left: '32%', delay: 1.9, s: 13 },
  { top: '20%', left: '90%', delay: 3.1, s: 9 },
  { top: '82%', left: '60%', delay: 2.7, s: 12 },
  { top: '46%', left: '48%', delay: 4, s: 8 },
];

const PARTICLES = Array.from({ length: 12 }, (_, i) => ({
  left: `${6 + i * 7}%`,
  delay: i * 0.55,
  duration: 8 + (i % 5) * 1.8,
  size: 2 + (i % 4),
}));

function CrystalMark({ size = 48, color = '#C9A05A', animate = true }) {
  const rays = [
    { rot: -36, h: 0.72, o: 0.55 },
    { rot: -18, h: 0.9, o: 0.75 },
    { rot: 0, h: 1, o: 0.95 },
    { rot: 18, h: 0.9, o: 0.75 },
    { rot: 36, h: 0.72, o: 0.55 },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size * 1.15,
      }}
    >
      {rays.map((ray, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            bottom: '8%',
            left: '50%',
            width: size * 0.16,
            height: size * ray.h,
            ml: `-${size * 0.08}px`,
            transformOrigin: '50% 100%',
            transform: `rotate(${ray.rot}deg)`,
            opacity: ray.o,
            background: `linear-gradient(180deg,
              ${alpha('#ffffff', 0.95)} 0%,
              ${alpha(color, 0.85)} 35%,
              ${alpha(color, 0.35)} 100%)`,
            clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)',
            borderRadius: 1,
          }}
        />
      ))}
      {animate && (
        <Box
          sx={{
            position: 'absolute',
            top: '18%',
            left: '42%',
            width: '16%',
            height: '28%',
            background: `linear-gradient(180deg, ${alpha('#ffffff', 0.9)}, transparent)`,
            clipPath: 'polygon(30% 0%, 100% 0%, 70% 100%, 0% 100%)',
            animation: `${facetShine} 3.5s ease-in-out infinite`,
          }}
        />
      )}
    </Box>
  );
}

function Diamond({ size = 28, color = '#E8C878' }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        position: 'relative',
        transform: 'rotate(45deg)',
        background: `linear-gradient(135deg,
          ${alpha('#ffffff', 0.9)} 0%,
          ${alpha(color, 0.55)} 40%,
          ${alpha(color, 0.25)} 70%,
          ${alpha('#ffffff', 0.5)} 100%)`,
        border: `1px solid ${alpha('#ffffff', 0.65)}`,
        boxShadow: `0 6px 20px ${alpha(color, 0.28)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '18%',
          background: `linear-gradient(135deg, ${alpha('#ffffff', 0.7)}, transparent)`,
          border: `1px solid ${alpha('#ffffff', 0.35)}`,
        },
      }}
    />
  );
}

function GlassRing({ size }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: `1.5px solid ${alpha('#ffffff', 0.45)}`,
        background: `radial-gradient(circle at 30% 30%,
          ${alpha('#ffffff', 0.35)} 0%,
          ${alpha('#E8C878', 0.08)} 40%,
          transparent 70%)`,
        boxShadow: `0 0 24px ${alpha('#D4A574', 0.15)}`,
        animation: `${breathe} 6s ease-in-out infinite`,
      }}
    />
  );
}

function Sparkle({ size = 12 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        position: 'relative',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          background: `linear-gradient(180deg, #fff, ${alpha('#E8C878', 0.8)})`,
          borderRadius: 2,
        },
        '&::before': {
          width: 2,
          height: size,
          left: (size - 2) / 2,
          top: 0,
        },
        '&::after': {
          width: size,
          height: 2,
          left: 0,
          top: (size - 2) / 2,
        },
      }}
    />
  );
}

const shellSx = {
  position: 'fixed',
  inset: 0,
  zIndex: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
  contain: 'paint',
  transform: 'translateZ(0)',
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden',
};

const MOBILE_BLOOMS = [
  { size: '42vw', top: '-8%', left: '-16%', color: '#E8C878', opacity: 0.42 },
  { size: '38vw', top: '18%', right: '-14%', color: '#F5E6C8', opacity: 0.34 },
  { size: '48vw', bottom: '-6%', right: '-18%', color: '#D4A574', opacity: 0.36 },
  { size: '34vw', bottom: '28%', left: '-12%', color: '#B8894A', opacity: 0.22 },
];

const MOBILE_CRYSTALS = [
  { top: '10%', right: '8%', size: 28, rotate: -16, opacity: 0.32 },
  { top: '36%', left: '6%', size: 22, rotate: 28, opacity: 0.24 },
  { bottom: '18%', right: '12%', size: 24, rotate: 12, opacity: 0.26 },
];

/** Lightweight static glass look — safe for mobile scroll */
function MobileBackground({ isDark }) {
  const gold = isDark ? '#D4A574' : '#C9A05A';

  return (
    <Box
      aria-hidden
      sx={{
        ...shellSx,
        width: '100vw',
        height: '100vh',
        minHeight: '-webkit-fill-available',
        '@supports (height: 100dvh)': {
          height: '100dvh',
        },
        background: isDark
          ? 'linear-gradient(165deg, #0E0C0A 0%, #17130F 38%, #221A12 72%, #1A1510 100%)'
          : 'linear-gradient(165deg, #FFFEFB 0%, #FBF6EC 28%, #F4E6CC 58%, #F7ECD8 82%, #FBF7F0 100%)',
      }}
    >
      {MOBILE_BLOOMS.map((b, i) => (
        <Box
          key={`m-bloom-${i}`}
          sx={{
            position: 'absolute',
            width: b.size,
            height: b.size,
            maxWidth: 220,
            maxHeight: 220,
            borderRadius: '50%',
            top: b.top,
            left: b.left,
            right: b.right,
            bottom: b.bottom,
            background: `radial-gradient(circle,
              ${alpha(b.color, isDark ? b.opacity * 0.85 : b.opacity)} 0%,
              ${alpha('#ffffff', isDark ? 0.04 : 0.18)} 45%,
              transparent 72%)`,
          }}
        />
      ))}

      <Box
        sx={{
          position: 'absolute',
          top: '28%',
          left: '50%',
          width: 'min(70vw, 280px)',
          height: 'min(70vw, 280px)',
          transform: 'translateX(-50%)',
          borderRadius: '50%',
          background: `radial-gradient(circle at 35% 30%,
            ${alpha('#ffffff', isDark ? 0.08 : 0.42)} 0%,
            ${alpha('#E8C878', isDark ? 0.08 : 0.16)} 42%,
            transparent 70%)`,
          border: `1px solid ${alpha('#ffffff', isDark ? 0.06 : 0.28)}`,
          opacity: isDark ? 0.55 : 0.7,
        }}
      />

      {MOBILE_CRYSTALS.map((c, i) => (
        <Box
          key={`m-crystal-${i}`}
          sx={{
            position: 'absolute',
            top: c.top,
            left: c.left,
            right: c.right,
            bottom: c.bottom,
            opacity: isDark ? c.opacity * 0.9 : c.opacity,
            transform: `rotate(${c.rotate}deg)`,
          }}
        >
          {i === 1 ? (
            <Diamond size={c.size * 0.7} color={gold} />
          ) : (
            <CrystalMark size={c.size} color={gold} animate={false} />
          )}
        </Box>
      ))}

      <Box
        sx={{
          position: 'absolute',
          top: '16%',
          left: '42%',
          opacity: isDark ? 0.35 : 0.45,
        }}
      >
        <Sparkle size={10} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: '30%',
          right: '28%',
          opacity: isDark ? 0.3 : 0.4,
        }}
      >
        <Sparkle size={8} />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: isDark
            ? `linear-gradient(125deg, ${alpha('#E8C878', 0.07)}, transparent 42%, ${alpha('#B8894A', 0.08)})`
            : `linear-gradient(125deg, ${alpha('#ffffff', 0.55)}, transparent 40%, ${alpha('#E8C878', 0.14)})`,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.05 : 0.07,
          backgroundImage: `
            linear-gradient(118deg, transparent 42%, ${alpha('#ffffff', 0.75)} 50%, transparent 58%),
            linear-gradient(72deg, transparent 40%, ${alpha('#E8C878', 0.45)} 50%, transparent 60%)
          `,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: isDark
            ? `radial-gradient(ellipse 90% 70% at 50% 40%, transparent 45%, ${alpha('#000', 0.35)} 100%)`
            : `radial-gradient(ellipse 90% 70% at 50% 40%, transparent 50%, ${alpha('#B8894A', 0.06)} 100%)`,
        }}
      />
    </Box>
  );
}

function DesktopBackground({ isDark, gold }) {
  return (
    <Box
      aria-hidden
      sx={{
        ...shellSx,
        background: isDark
          ? 'linear-gradient(160deg, #100e0b 0%, #1a1510 45%, #241c14 100%)'
          : `linear-gradient(160deg,
              #FFFEFB 0%,
              #FBF5EA 22%,
              #F3E4C8 48%,
              #F7ECD8 72%,
              #FBF7F0 100%)`,
        '@media (prefers-reduced-motion: reduce)': {
          '& *': { animation: 'none !important' },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: '-10%',
          opacity: isDark ? 0.35 : 0.55,
          backgroundImage: `
            radial-gradient(ellipse 40% 30% at 20% 30%, ${alpha('#E8C878', 0.45)}, transparent 60%),
            radial-gradient(ellipse 35% 35% at 75% 65%, ${alpha('#D4A574', 0.35)}, transparent 55%),
            radial-gradient(ellipse 30% 25% at 50% 20%, ${alpha('#ffffff', 0.5)}, transparent 50%)
          `,
          backgroundSize: '140% 140%, 160% 160%, 120% 120%',
          animation: `${caustic} 18s ease-in-out infinite`,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: isDark
            ? `linear-gradient(125deg, ${alpha('#E8C878', 0.08)}, transparent 40%, ${alpha('#B8894A', 0.1)})`
            : `linear-gradient(125deg, ${alpha('#ffffff', 0.65)}, transparent 38%, ${alpha('#E8C878', 0.2)})`,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: '-5%',
          left: 0,
          width: '55%',
          height: '120%',
          background: `linear-gradient(105deg,
            transparent 30%,
            ${alpha('#ffffff', 0.35)} 45%,
            ${alpha('#E8C878', 0.25)} 50%,
            ${alpha('#D4A574', 0.15)} 55%,
            transparent 70%)`,
          animation: `${prismSweep} 14s ease-in-out infinite`,
        }}
      />

      {BLOOMS.map((b, i) => (
        <Box
          key={`bloom-${i}`}
          sx={{
            position: 'absolute',
            width: b.size,
            height: b.size,
            borderRadius: '50%',
            top: b.top,
            left: b.left,
            right: b.right,
            background: `radial-gradient(circle,
              ${alpha(b.color, isDark ? 0.35 : 0.4)} 0%,
              ${alpha('#ffffff', isDark ? 0.05 : 0.25)} 42%,
              transparent 70%)`,
            animation: `${pulseGlow} ${6 + b.delay}s ease-in-out infinite`,
            animationDelay: `${b.delay}s`,
          }}
        />
      ))}

      {RINGS.map((r, i) => (
        <Box
          key={`ring-${i}`}
          sx={{
            position: 'absolute',
            top: r.top,
            left: r.left,
            animation: `${drift} ${18 + i * 3}s ease-in-out infinite`,
            animationDelay: `${r.delay}s`,
            '--r': `${i * 20}deg`,
          }}
        >
          <GlassRing size={r.size} />
        </Box>
      ))}

      {CRYSTALS.map((c, i) => (
        <Box
          key={`crystal-${i}`}
          sx={{
            position: 'absolute',
            top: c.top,
            left: c.left,
            '--r': `${c.rotate}deg`,
            animation: `${drift} ${c.duration}s ease-in-out infinite, ${facetShine} ${3.5 + (i % 3)}s ease-in-out infinite`,
            animationDelay: `${c.delay}s, ${c.delay * 0.5}s`,
          }}
        >
          {i % 3 === 1 ? (
            <Diamond size={c.size * 0.65} color={gold} />
          ) : (
            <CrystalMark size={c.size} color={gold} />
          )}
        </Box>
      ))}

      <Box
        sx={{
          position: 'absolute',
          top: '35%',
          left: '50%',
          width: 320,
          height: 320,
          ml: '-160px',
          borderRadius: '50%',
          background: `radial-gradient(circle at 35% 30%,
            ${alpha('#ffffff', isDark ? 0.12 : 0.55)} 0%,
            ${alpha('#E8C878', isDark ? 0.08 : 0.18)} 45%,
            transparent 70%)`,
          border: `1px solid ${alpha('#ffffff', isDark ? 0.08 : 0.4)}`,
          animation: `${spinSlow} 60s linear infinite`,
          opacity: 0.7,
        }}
      />

      {SPARKLES.map((s, i) => (
        <Box
          key={`spark-${i}`}
          sx={{
            position: 'absolute',
            top: s.top,
            left: s.left,
            animation: `${sparkBurst} ${2.8 + (i % 4) * 0.5}s ease-in-out infinite`,
            animationDelay: `${s.delay}s`,
          }}
        >
          <Sparkle size={s.s} />
        </Box>
      ))}

      {PARTICLES.map((p, i) => (
        <Box
          key={`dust-${i}`}
          sx={{
            position: 'absolute',
            bottom: '-4%',
            left: p.left,
            width: p.size,
            height: p.size,
            borderRadius: i % 3 === 0 ? '1px' : '50%',
            bgcolor: i % 2 === 0 ? alpha('#ffffff', 0.85) : alpha('#E8C878', 0.75),
            transform: i % 3 === 0 ? 'rotate(45deg)' : undefined,
            animation: `${floatUp} ${p.duration}s linear infinite`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.07 : 0.09,
          backgroundImage: `
            linear-gradient(118deg, transparent 42%, ${alpha('#ffffff', 0.8)} 49.5%, transparent 57%),
            linear-gradient(72deg, transparent 38%, ${alpha('#E8C878', 0.55)} 50%, transparent 62%)
          `,
        }}
      />
    </Box>
  );
}

export default function PharmacyBackground() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const gold = isDark ? '#D4A574' : '#C9A05A';

  if (isMobile) {
    return <MobileBackground isDark={isDark} />;
  }

  return <DesktopBackground isDark={isDark} gold={gold} />;
}
